// @@CLIENT_PROFILE_v102@@ — Client profile, rebuilt.
//   Header (name, status, designation, Zoho, quick links, Edit) → summary tiles →
//   tabs: Overview · Contacts & portal · Shopify & reports · Shipping · Shows · W9 & payment.
//   Tab lives in ?tab= so it's linkable and survives refresh. Edit opens a labeled,
//   grouped dialog instead of a row of unlabeled inputs.
//   Every endpoint, role gate and behavior from the previous page is preserved:
//   @@SHOPIFY_COGS_INVENTORY_v57@@ @@SHOPIFY_REPORT_TOGGLES_v58@@ @@SHOPIFY_REPORT_ENHANCEMENTS_v59@@
//   @@CREDIT_NOTE_CLIENT_v1@@ @@V61_PAYOUT_UI@@ @@V71_REPORT_TAX_INTL@@ @@FEDEX_LABELS_v60p2@@
//   @@MPS_v65p2@@ @@PARCEL_PRESETS_v63p2@@ @@LABEL_HISTORY_v61p2@@ @@ZPL_UI_v62p2@@
//   @@CLIENT_INTEGRATIONS_v53_UI@@ @@SHOPIFY_CC_v55_UI@@ @@SHOPIFY_REPORTS_v56_UI@@
//   @@OPENING_BALANCE_UI@@ @@SHIPPING_TOGGLE_v1@@ @@SECURITY_v90@@ @@PAYMENT_INFO_v82@@
//   New: portal status per contact + Invite / Resend (POST /api/contacts/:id/invite).
//   Fix: all hooks now run before any early return (the old page declared Shopify
//   hooks after a conditional return — a rules-of-hooks crash on "Client not found").
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PaymentInfoStaffPanel } from '../components/PaymentInfo';
import CnAccountPicker from '../components/CnAccountPicker'; // @@CN_ACCOUNT_v108@@
import ReportsReview from '../components/ReportsReview'; // @@REPORT_REVIEW_v115@@
import NotifyClientModal from '../components/NotifyClientModal'; // @@NOTIFY_PICK_v115_4@@
import { periodOptions, resolvePeriod } from '../lib/reportPeriods'; // @@REPORT_PERIODS_v115_1@@
import CsvReportPanel from '../components/CsvReportPanel'; // @@CSV_REPORTS_v117@@

const STATUSES = ['prospect', 'active', 'paused', 'archived'];
const BLANK_BOX = { weight_lbs: '', length_in: '', width_in: '', height_in: '' };
const BLANK_STORE = { store_domain: '', shopify_client_id: '', label: '', access_token: '', api_scopes: '' };
const DASH = '—';

function money(v) {
  return v == null ? DASH : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function shortDate(d) {
  if (!d) return DASH;
  const x = new Date(d);
  return isNaN(x) ? DASH : x.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function dateTime(d) {
  if (!d) return DASH;
  const x = new Date(d);
  return isNaN(x) ? DASH : x.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
function personName(c) { return `${c.first_name || ''} ${c.last_name || ''}`.trim(); }
async function saveBlob(res, fallbackName) {
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function Ext({ href, children }) {
  if (!href) return <span className="hq-cd-muted">{DASH}</span>;
  return <a className="hq-cd-a" href={href} target="_blank" rel="noreferrer">{children}</a>;
}

function PortalPill({ c }) {
  if (c.portal_active) return <span className="hq-pill hq-cstatus-active">Portal active</span>;
  if (c.portal_invited) return <span className="hq-pill hq-cstatus-paused">Invite pending</span>;
  return <span className="hq-pill hq-cstatus-archived">No access</span>;
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileRef = useRef(null);

  const canManageLinks = !!user && (user.role === 'admin' || user.role === 'manager');
  const canReadW9 = canManageLinks;

  // ── core ──
  const [client, setClient] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [msg, setMsg] = useState('');
  const [allContacts, setAllContacts] = useState([]);
  const [pick, setPick] = useState('');
  const [inviteBusy, setInviteBusy] = useState({});
  // ── edit dialog ──
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editMsg, setEditMsg] = useState('');
  const [saving, setSaving] = useState(false);
  // ── events ──
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(false);
  // ── Zoho / Shopify ──
  const [zohoCustomers, setZohoCustomers] = useState([]);
  const [zohoPick, setZohoPick] = useState('');
  const [storeForm, setStoreForm] = useState(BLANK_STORE);
  const [showAddStore, setShowAddStore] = useState(false);
  const [salesByStore, setSalesByStore] = useState({});
  const [reportsByStore, setReportsByStore] = useState({});
  const [reportOpts, setReportOpts] = useState({ cogs: false, inventory: false });
  const [reportForm, setReportForm] = useState({ period_type: 'month', period_start: '' });
  const [reportBusy, setReportBusy] = useState(false);
  const [cnBusy, setCnBusy] = useState({});
  // @@REPORT_REVIEW_v115@@ notify busy flags + "just generated" focus (storeId -> { id, t }) for <ReportsReview>
  const [notifyBusy, setNotifyBusy] = useState({});
  const [focusReport, setFocusReport] = useState({});
  const [linkBusy, setLinkBusy] = useState({}); // @@CN_LINK_EXISTING_v115_3@@
  const [notifyDlg, setNotifyDlg] = useState(null);   // @@NOTIFY_PICK_v115_4@@ { storeId, rep } while the picker is open
  const [notifyErr, setNotifyErr] = useState('');
  // ── shipping ──
  const [labelForm, setLabelForm] = useState({ description: '' });
  const [boxes, setBoxes] = useState([{ ...BLANK_BOX }]);
  const [parcelPresets, setParcelPresets] = useState([]);
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelMsg, setLabelMsg] = useState('');
  const [shipments, setShipments] = useState([]);

  // ── loaders ──
  async function load() {
    const res = await apiFetch(`/api/clients/${id}`);
    if (res.ok) { const d = await res.json(); setClient(d.client); setNotFound(false); }
    else if (res.status === 404) setNotFound(true);
  }
  async function loadContacts() {
    const res = await apiFetch('/api/contacts');
    if (res.ok) { const d = await res.json(); setAllContacts(d.contacts || []); }
  }
  async function loadShipments() {
    const res = await apiFetch(`/api/clients/${id}/packages`);
    if (res.ok) { const d = await res.json(); setShipments(d.packages || []); }
  }
  async function loadParcelPresets() {
    const res = await apiFetch('/api/packages/parcel-presets');
    if (res.ok) { const d = await res.json(); setParcelPresets(d.presets || []); }
  }
  async function loadZohoCustomers() {
    const res = await apiFetch('/api/clients/zoho-customers');
    if (res.ok) { const d = await res.json(); setZohoCustomers(d.customers || []); }
  }
  useEffect(() => {
    setClient(null); setMsg(''); setSalesByStore({}); setReportsByStore({}); setFocusReport({});
    load(); loadContacts(); loadShipments();
  }, [id]);
  useEffect(() => { loadParcelPresets(); }, []);
  useEffect(() => { if (canManageLinks) loadZohoCustomers(); }, [canManageLinks]);

  // Esc closes the edit dialog
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setEditing(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  // ── tabs ──
  const TABS = useMemo(() => {
    const t = [
      { key: 'overview', label: 'Overview' },
      { key: 'people', label: 'Contacts & portal' },
    ];
    if (canManageLinks) t.push({ key: 'shopify', label: 'Shopify & reports' });
    t.push({ key: 'shipping', label: 'Shipping' });
    t.push({ key: 'shows', label: 'Shows' });
    t.push({ key: 'docs', label: 'W9 & payment' });
    return t;
  }, [canManageLinks]);
  const wanted = searchParams.get('tab');
  const tab = TABS.some((t) => t.key === wanted) ? wanted : 'overview';
  function goTab(k) {
    const p = new URLSearchParams(searchParams);
    if (k === 'overview') p.delete('tab'); else p.set('tab', k);
    setSearchParams(p, { replace: true });
    setMsg('');
  }

  // ── status / edit ──
  async function updateStatus(status) {
    setMsg('');
    const res = await apiFetch(`/api/clients/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) { const d = await res.json(); setClient((c) => ({ ...c, ...d.client })); }
    else setMsg('Could not change status.');
  }
  function beginEdit() {
    setEditMsg('');
    setEdit({
      name: client.name || '', genre: client.genre || '', hometown: client.hometown || '',
      shopify_url: client.shopify_url || '', drive_url: client.drive_url || '',
      bandsintown_artist_id: client.bandsintown_artist_id || '', notes: client.notes || '',
      shows_source: client.shows_source === 'seated' ? 'seated' : 'bandsintown', seated_artist_id: client.seated_artist_id || '', // @@SEATED_SHOWS_v114@@
      status: client.status || 'active',
      commission_pct: client.commission_pct == null ? 20 : client.commission_pct,
      designation_code: client.designation_code || '',
      opening_balance: client.opening_balance == null ? '' : client.opening_balance,
      opening_balance_date: client.opening_balance_date ? String(client.opening_balance_date).slice(0, 10) : '',
      shipping_enabled: !!client.shipping_enabled,
    });
    setEditing(true);
  }
  async function saveEdits() {
    setEditMsg(''); setSaving(true);
    try {
      const res = await apiFetch(`/api/clients/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edit),
      });
      if (res.ok) {
        const d = await res.json(); setClient((c) => ({ ...c, ...d.client })); setEditing(false);
        // @@SEATED_SHOWS_v114_1@@ show source / artist ID changed -> the server already refreshed; reload + say what happened.
        if (d.shows_refresh) {
          await load();
          const r = d.shows_refresh;
          setMsg(r.ok
            ? `Saved. Shows refreshed: ${r.count} found.`
            : d.client && d.client.shows_source === 'seated'
            ? 'Saved. ' + seatedFailText(r.reason, r.detail)
            : 'Saved, but shows could not be fetched. Check the Bandsintown key and artist ID.');
        }
      }
      else { const d = await res.json().catch(() => ({})); setEditMsg(d.error || 'Could not save changes.'); }
    } finally { setSaving(false); }
  }
  const setE = (k) => (e) => setEdit((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // ── contacts & portal ──
  async function attach() {
    if (!pick) return;
    const res = await apiFetch(`/api/clients/${id}/contacts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: pick }),
    });
    if (res.ok) { setPick(''); load(); }
    else setMsg('Could not attach contact.');
  }
  async function detach(c) {
    if (c.portal_invited && !window.confirm(`Remove ${personName(c) || 'this contact'} from this band? Their portal login will no longer see this band.`)) return;
    const res = await apiFetch(`/api/clients/${id}/contacts/${c.id}`, { method: 'DELETE' });
    if (res.ok) load(); else setMsg('Could not remove contact.');
  }
  async function invite(c) {
    setMsg('');
    setInviteBusy((m) => ({ ...m, [c.id]: true }));
    try {
      const res = await apiFetch(`/api/contacts/${c.id}/invite`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(d.invite_sent
          ? `Portal invite emailed to ${c.email}.`
          : `Invite created for ${c.email}, but the email didn't send (check Resend).`);
        load();
      } else setMsg(d.error || 'Could not send invite.');
    } finally { setInviteBusy((m) => ({ ...m, [c.id]: false })); }
  }

  // ── Zoho ──
  async function refreshZohoCustomers() {
    setMsg('');
    const res = await apiFetch('/api/clients/zoho-customers/refresh', { method: 'POST' });
    if (res.ok) { await loadZohoCustomers(); setMsg('Zoho customers refreshed.'); }
    else {
      const d = await res.json().catch(() => ({}));
      setMsg(d.error === 'zoho not configured' ? 'Zoho is not configured.' : (d.error || 'Refresh failed.'));
    }
  }
  async function linkZoho() {
    setMsg('');
    const res = await apiFetch(`/api/clients/${id}/zoho-link`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ zoho_contact_id: zohoPick }),
    });
    if (res.ok) { const d = await res.json(); setClient((c) => ({ ...c, ...d.client })); setZohoPick(''); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not link.'); }
  }
  async function createAndLinkZoho() {
    setMsg('');
    const res = await apiFetch(`/api/clients/${id}/zoho-create-link`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: client.name }),
    });
    if (res.ok) { const d = await res.json(); setClient((c) => ({ ...c, ...d.client })); await loadZohoCustomers(); }
    else {
      const d = await res.json().catch(() => ({}));
      setMsg(d.reason === 'unconfigured' ? 'Zoho is not configured.' : (d.message || d.error || 'Create failed.'));
    }
  }

  // ── Shopify ──
  async function addStore() {
    setMsg('');
    if (!storeForm.store_domain.trim()) { setMsg('Store domain required.'); return; }
    const res = await apiFetch(`/api/clients/${id}/shopify-stores`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(storeForm),
    });
    if (res.ok) { setStoreForm(BLANK_STORE); setShowAddStore(false); await load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not add store.'); }
  }
  async function removeStore(storeId) {
    if (!window.confirm('Remove this Shopify store?')) return;
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}`, { method: 'DELETE' });
    if (res.ok) await load();
  }
  async function replaceStoreToken(storeId) {
    const t = window.prompt('Paste new Shopify Admin API token (leave blank to cancel):');
    if (!t || !t.trim()) return;
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: t.trim() }),
    });
    if (res.ok) { await load(); setMsg('Token updated.'); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not update token.'); }
  }
  async function viewSales(storeId) {
    setMsg('');
    if (salesByStore[storeId]) { setSalesByStore((p) => { const n = { ...p }; delete n[storeId]; return n; }); return; }
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/sales?months=12`);
    if (res.ok) {
      const d = await res.json();
      setSalesByStore((prev) => ({ ...prev, [storeId]: { rows: d.rows || [], columns: d.columns || [] } }));
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg(d.error === 'could not reach Shopify' ? 'Could not reach Shopify (check credentials).'
        : d.error === 'store missing Shopify credentials' ? 'Add a Client ID and secret first.'
        : (d.error || 'Could not load sales.'));
    }
  }
  async function loadReports(storeId) {
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports`);
    if (res.ok) { const d = await res.json(); setReportsByStore((p) => ({ ...p, [storeId]: d.reports || [] })); }
  }
  function toggleReports(storeId) {
    if (reportsByStore[storeId]) {
      setReportsByStore((p) => { const n = { ...p }; delete n[storeId]; return n; });
    } else loadReports(storeId);
  }
  async function generateReport(storeId) {
    setMsg('');
    // @@REPORT_PERIODS_v115_1@@ the period comes from a list (default = last completed period); no typed dates.
    const existing = reportsByStore[storeId] || [];
    const period_start = resolvePeriod(reportForm.period_type, reportForm.period_start, existing);
    const picked = periodOptions(reportForm.period_type, existing).options.find((o) => o.value === period_start);
    if (picked && picked.done && !window.confirm(`${picked.label} was already generated. Regenerate it with fresh figures?\n\nIf it already has a credit note, the report line will warn when the amount changed.`)) return;
    setReportBusy(true);
    try {
      const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_type: reportForm.period_type, period_start, cogs: !!reportOpts.cogs }), // @@COGS_PAYOUT_v116@@ COGS tick also deducts COGS from the payout at Generate
      });
      if (res.ok) { const d = await res.json(); await loadReports(storeId); setFocusReport((p) => ({ ...p, [storeId]: { id: d.report.id, t: Date.now() } })); }
      else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Report generation failed.'); }
    } finally { setReportBusy(false); }
  }
  async function downloadReportPdf(storeId, reportId) {
    setMsg('');
    try {
      const params = [];
      if (reportOpts.cogs) params.push('cogs=1');
      if (reportOpts.inventory) params.push('inventory=1');
      const qs = params.length ? ('?' + params.join('&')) : '';
      const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${reportId}/pdf${qs}`);
      if (!res.ok) { setMsg('Could not generate PDF.'); return; }
      await saveBlob(res, 'sales-report.pdf');
    } catch (e) { setMsg('Could not download PDF.'); }
  }
  // @@REPORT_REVIEW_v115@@ creates the Zoho credit note; the server now LINKS it to the report. If one is already linked the
  // server answers 409 and we ask before creating another (force).
  async function generateCreditNote(storeId, reportId, force) {
    setMsg('');
    setCnBusy((m) => ({ ...m, [reportId]: true }));
    let askAbout = null;
    try {
      const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${reportId}/credit-note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(force ? { force: true } : {}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.credit_note) {
        const num = d.credit_note.creditnote_number || d.credit_note.reference_number || '';
        setMsg(d.reused
          ? `Found credit note ${num} already in Zoho${d.linked === false ? ' — but the link could not be saved on this report' : ' and linked it to this report'}.`
          : `Draft credit note ${num} created in Zoho${d.linked === false ? ' — but the link could not be saved on this report' : ''}.`.replace(/\s+/g, ' ').trim());
        await loadReports(storeId);
      } else if (res.status === 409 && d.code === 'already_linked') {
        askAbout = (d.credit_note && d.credit_note.number) || 'a credit note';
      } else setMsg(d.message || d.error || 'Could not create credit note.');
    } catch (e) {
      setMsg('Could not create credit note.');
    } finally { setCnBusy((m) => ({ ...m, [reportId]: false })); }
    if (askAbout && window.confirm(`This report is already linked to ${askAbout}. Create another credit note in Zoho?`)) {
      await generateCreditNote(storeId, reportId, true);
    }
  }
  // @@REPORT_REVIEW_v115@@ email the client's portal users that this report is ready (+ upcoming-shows nudge, server side).
  // @@NOTIFY_PICK_v115_4@@ Notify opens a picker (who gets the email); sendNotify does the send with the chosen contact ids.
  function notifyReport(storeId, rep) {
    setMsg(''); setNotifyErr('');
    setNotifyDlg({ storeId, rep });
  }
  // @@NOTIFY_OPTIONS_v115_7@@ server-rendered preview of the exact email (nothing is sent)
  async function previewNotify({ ids, includeShows, note }) {
    if (!notifyDlg) return null;
    const { storeId, rep } = notifyDlg;
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${rep.id}/notify/preview`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients: ids, include_shows: includeShows, note }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Could not build the preview.');
    return d;
  }
  async function sendNotify(opts) {
    if (!notifyDlg) return;
    const { storeId, rep } = notifyDlg;
    setNotifyErr('');
    setNotifyBusy((m) => ({ ...m, [rep.id]: true }));
    try {
      const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${rep.id}/notify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resend: !!rep.client_notified_at, recipients: opts.ids, include_shows: opts.includeShows, note: opts.note }), // @@NOTIFY_OPTIONS_v115_7@@
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setNotifyDlg(null);
        setMsg(`Emailed ${d.sent} ${d.sent === 1 ? 'person' : 'people'}` +
          (d.sent < d.recipients ? ` (${d.recipients - d.sent} could not be sent)` : '') +
          (d.shows ? ` — the email includes ${d.shows} upcoming show${d.shows === 1 ? '' : 's'} (next 45 days).` : '.'));
        await loadReports(storeId);
      } else setNotifyErr(d.error || 'Could not send the notification.');
    } catch (e) {
      setNotifyErr('Could not send the notification.');
    } finally { setNotifyBusy((m) => ({ ...m, [rep.id]: false })); }
  }
  // @@CN_LINK_EXISTING_v115_3@@ link credit notes that already exist in Zoho to every report that has none. Lookup + link only:
  // the server never creates anything in this mode ({ link_only:true }).
  async function linkExistingCreditNotes(storeId) {
    setMsg('');
    const todo = (reportsByStore[storeId] || []).filter((r) => !r.credit_note);
    if (!todo.length) { setMsg('Every report already has a credit note linked.'); return; }
    setLinkBusy((m) => ({ ...m, [storeId]: true }));
    let linked = 0; let none = 0; let failed = 0; let lastErr = '';
    try {
      for (const r of todo) {
        const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${r.id}/credit-note`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ link_only: true }),
        });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.credit_note) linked += 1;
        else if (res.status === 404 && d.code === 'not_found_in_zoho') none += 1;
        else { failed += 1; lastErr = d.error || ('HTTP ' + res.status); }
      }
    } catch (e) { failed += 1; lastErr = 'network error'; }
    finally { setLinkBusy((m) => ({ ...m, [storeId]: false })); }
    await loadReports(storeId);
    setMsg(`Linked ${linked} existing credit note${linked === 1 ? '' : 's'} from Zoho; ${none} report${none === 1 ? ' has' : 's have'} none yet.` +
      (failed ? ` ${failed} could not be checked (${lastErr}).` : ''));
  }
  async function deleteReport(storeId, reportId) {
    if (!window.confirm('Delete this report? This cannot be undone.')) return;
    const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/reports/${reportId}`, { method: 'DELETE' });
    if (res.ok) { await loadReports(storeId); }
    else setMsg('Could not delete report.');
  }
  async function downloadInventoryPdf(storeId) {
    setMsg('');
    try {
      const res = await apiFetch(`/api/clients/${id}/shopify-stores/${storeId}/inventory-pdf`);
      if (!res.ok) { setMsg('Could not generate inventory report (check Shopify credentials).'); return; }
      await saveBlob(res, 'inventory.pdf');
    } catch (e) { setMsg('Could not download inventory report.'); }
  }

  // ── events ──
  // @@SEATED_SHOWS_v114_1@@ plain-language text for a Seated fetch failure (reason codes from lib/seated.js).
  function seatedFailText(reason, detail) {
    const map = {
      no_artist_id: 'No Seated artist ID saved. Paste the artist UUID (or a Seated URL that contains it) in Edit > Links.',
      http_404: 'Seated returned 404: that ID is not a public Seated artist. Copy the UUID from the Seated dashboard URL.',
      http_403: 'Seated refused the request (403). It may be blocking requests from our server.',
      http_429: 'Seated is rate-limiting requests (429). Try again in a few minutes.',
      network: 'Could not reach Seated.',
      bad_body: 'Seated answered, but not with tour data.',
    };
    const r = String(reason || '');
    const base = map[r] || (r.startsWith('http_') ? `Seated returned HTTP ${r.slice(5)}.` : 'Refresh failed.');
    return detail && !base.includes(detail) ? `${base} (${detail})` : base;
  }
  async function refreshEvents() {
    setBusy(true); setMsg('');
    try {
      const res = await apiFetch(`/api/clients/${id}/refresh`, { method: 'POST' });
      if (res.ok) { // @@SEATED_SHOWS_v114_1@@ say so when the source answered with zero shows
        const d = await res.json().catch(() => ({}));
        await load();
        if (d.count === 0) setMsg('Refreshed. The source returned no upcoming shows for this artist.');
      }
      else {
        const d = await res.json().catch(() => ({}));
        setMsg(client.shows_source === 'seated'
          ? seatedFailText(d.reason, d.detail)
          : d.reason === 'no_data'
          ? 'No events fetched — set an API key (admin/manager) and a Bandsintown artist ID, then refresh.'
          : (d.error || 'Refresh failed.'));
      }
    } finally { setBusy(false); }
  }
  async function saveKey() {
    setMsg('');
    const res = await apiFetch(`/api/clients/${id}/bandsintown-key`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bandsintown_app_id: keyInput }),
    });
    if (res.ok) { setKeyInput(''); await load(); setMsg('Bandsintown key saved.'); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not save key.'); }
  }

  // ── W9 ──
  async function uploadW9(file) {
    if (!file) return;
    setMsg('');
    if (file.type && file.type !== 'application/pdf') { setMsg('W9 must be a PDF.'); return; }
    const res = await apiFetch(`/api/clients/${id}/w9`, {
      method: 'POST', headers: { 'Content-Type': 'application/pdf', 'X-Filename': file.name },
      body: file,
    });
    if (res.ok) load();
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Upload failed.'); }
  }
  async function downloadW9(w9) {
    const res = await apiFetch(`/api/clients/${id}/w9/${w9.id}`);
    if (!res.ok) { setMsg('Could not download.'); return; }
    await saveBlob(res, w9.original_name || 'w9.pdf');
  }
  async function deleteW9(w9) {
    if (!window.confirm('Delete this W9?')) return;
    const res = await apiFetch(`/api/clients/${id}/w9/${w9.id}`, { method: 'DELETE' });
    if (res.ok) load();
  }

  // ── shipping ──
  function setBox(i, patch) { setBoxes((bs) => bs.map((bx, idx) => (idx === i ? { ...bx, ...patch } : bx))); }
  function addBox() { setBoxes((bs) => [...bs, { ...BLANK_BOX }]); }
  function removeBox(i) { setBoxes((bs) => (bs.length > 1 ? bs.filter((_, idx) => idx !== i) : bs)); }
  function applyParcelPresetToBox(i, presetId) {
    const p = parcelPresets.find((x) => x.id === presetId);
    if (!p) return;
    setBox(i, {
      weight_lbs: p.weight_lbs == null ? '' : String(p.weight_lbs),
      length_in: p.length_in == null ? '' : String(p.length_in),
      width_in: p.width_in == null ? '' : String(p.width_in),
      height_in: p.height_in == null ? '' : String(p.height_in),
    });
  }
  async function createLabel() {
    setLabelMsg(''); setLabelBusy(true);
    try {
      const body = { client_id: id };
      if (labelForm.description.trim()) body.description = labelForm.description.trim();
      body.parcels = boxes.map((bx) => {
        const o = {};
        ['weight_lbs', 'length_in', 'width_in', 'height_in'].forEach((k) => { if (String(bx[k]).trim() !== '') o[k] = bx[k]; });
        return o;
      });
      const res = await apiFetch('/api/packages/labels', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setLabelMsg(d.error || 'Could not create label.');
        return;
      }
      const tracking = res.headers.get('X-Tracking-Number') || '';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = (tracking || 'fedex-label') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLabelForm({ description: '' });
      setBoxes([{ ...BLANK_BOX }]);
      loadShipments();
      setLabelMsg(tracking ? ('Label created — tracking ' + tracking + '. Added to Packages.') : 'Label created. Added to Packages.');
    } catch (e) {
      setLabelMsg('Could not create label.');
    } finally { setLabelBusy(false); }
  }
  async function downloadShipmentLabel(pkg) {
    const res = await apiFetch(`/api/packages/${pkg.id}/label`);
    if (!res.ok) { setLabelMsg('Label file not available.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = (pkg.label || 'label') + (/(\.pdf|\.png)$/i.test(pkg.label || '') ? '' : '.pdf');
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  async function downloadShipmentZpl(pkg) {
    const res = await apiFetch(`/api/packages/${pkg.id}/label.zpl`);
    if (!res.ok) { setLabelMsg('ZPL file not available.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = (pkg.label || 'label') + '.zpl';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  async function deleteShipment(pkg) {
    if (!window.confirm('Permanently delete this shipment and its label file? The label PDF cannot be recovered or reprinted.')) return;
    const res = await apiFetch(`/api/packages/${pkg.id}`, { method: 'DELETE' });
    if (res.ok) { setLabelMsg('Shipment deleted.'); loadShipments(); }
    else setLabelMsg('Could not delete.');
  }

  // ───────────────────────────── render ─────────────────────────────
  if (notFound) {
    return (
      <div className="hq-page-content">
        <button className="hq-cd-back" onClick={() => navigate('/clients')}>← Clients</button>
        <div className="hq-page-head"><h1>Client not found</h1><p>It may have been deleted.</p></div>
      </div>
    );
  }
  if (!client) return <div className="hq-page-content"><div className="hq-page-head"><p>Loading…</p></div></div>;

  const contacts = client.contacts || [];
  const attached = new Set(contacts.map((c) => c.id));
  const available = allContacts.filter((c) => !attached.has(c.id));
  const portalActive = contacts.filter((c) => c.portal_active).length;
  const portalPending = contacts.filter((c) => c.portal_invited && !c.portal_active).length;
  const now = Date.now();
  const upcoming = (client.events || []).filter((e) => e.starts_at && new Date(e.starts_at).getTime() >= now - 12 * 3600 * 1000);
  const nextShow = upcoming[0] || null;
  const w9s = client.w9s || [];
  const stores = client.shopify_stores || [];
  const liveShipments = shipments.filter((p) => !p.archived_at);
  const commission = client.commission_pct == null ? 20 : client.commission_pct;
  const counts = { people: contacts.length, shopify: stores.length, shipping: liveShipments.length, shows: upcoming.length, docs: w9s.length };

  const Flash = msg ? (
    <div className="hq-cd-flash" role="status">
      <span>{msg}</span>
      <button className="hq-cd-x" onClick={() => setMsg('')} aria-label="Dismiss">×</button>
    </div>
  ) : null;

  return (
    <div className="hq-page-content hq-cd">
      <button className="hq-cd-back" onClick={() => navigate('/clients')}>← Clients</button>

      {/* ── header ── */}
      <header className="hq-cd-hero">
        <div className="hq-cd-avatar" aria-hidden="true">{initials(client.name)}</div>
        <div className="hq-cd-id">
          <div className="hq-cd-titlerow">
            <h1>{client.name || 'Untitled client'}</h1>
            {canReadW9 ? (
              <select className={`hq-cd-status hq-cstatus-${client.status}`} value={client.status}
                onChange={(e) => updateStatus(e.target.value)} aria-label="Client status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <span className={`hq-pill hq-cstatus-${client.status}`}>{client.status}</span>
            )}
          </div>
          <div className="hq-cd-meta">
            {[client.genre, client.hometown].filter(Boolean).join(' · ') || <span className="hq-cd-muted">No genre or hometown yet</span>}
            {client.designation_code && <span className="hq-cd-chip" title="Designation code">{client.designation_code}</span>}
          </div>
        </div>
        <div className="hq-cd-actions">
          {client.shopify_url && <a className="hq-btn hq-btn-ghost" href={client.shopify_url} target="_blank" rel="noreferrer">Shopify ↗</a>}
          {client.drive_url && <a className="hq-btn hq-btn-ghost" href={client.drive_url} target="_blank" rel="noreferrer">Drive ↗</a>}
          {canReadW9 && <button className="hq-btn" onClick={beginEdit}>Edit profile</button>}
        </div>
      </header>

      {/* ── summary tiles ── */}
      <div className="hq-cd-tiles">
        <button className="hq-cd-tile" onClick={() => goTab('people')}>
          <span className="hq-cd-k">Portal</span>
          <span className={'hq-cd-v' + (portalActive ? ' is-good' : '')}>{portalActive ? `${portalActive} active` : 'No logins'}</span>
          <span className="hq-cd-s">{portalPending ? `${portalPending} pending · ` : ''}{contacts.length} contact{contacts.length === 1 ? '' : 's'}</span>
        </button>
        <div className="hq-cd-tile">
          <span className="hq-cd-k">Zoho Books</span>
          <span className={'hq-cd-v' + (client.zoho_customer_id ? '' : ' is-warn')}>{client.zoho_customer_id ? 'Linked' : 'Not linked'}</span>
          <span className="hq-cd-s">{client.zoho_customer_name || (client.zoho_customer_id ? client.zoho_customer_id : 'Statements & invoices need a link')}</span>
        </div>
        <div className="hq-cd-tile">
          <span className="hq-cd-k">Commission</span>
          <span className="hq-cd-v">{commission}%</span>
          <span className="hq-cd-s">XYZ's cut of net sales</span>
        </div>
        <button className="hq-cd-tile" onClick={() => goTab('shipping')}>
          <span className="hq-cd-k">Client shipping</span>
          <span className={'hq-cd-v' + (client.shipping_enabled ? ' is-good' : '')}>{client.shipping_enabled ? 'On' : 'Off'}</span>
          <span className="hq-cd-s">{liveShipments.length} shipment{liveShipments.length === 1 ? '' : 's'} on record</span>
        </button>
        <button className="hq-cd-tile" onClick={() => goTab('shows')}>
          <span className="hq-cd-k">Next show</span>
          <span className="hq-cd-v hq-cd-v-sm">{nextShow ? shortDate(nextShow.starts_at) : 'None'}</span>
          <span className="hq-cd-s">{nextShow ? ([nextShow.venue_name, nextShow.city].filter(Boolean).join(' · ') || nextShow.title || '') : 'No upcoming shows cached'}</span>
        </button>
        <button className="hq-cd-tile" onClick={() => goTab('docs')}>
          <span className="hq-cd-k">W9 / W-8</span>
          <span className={'hq-cd-v' + (w9s.length ? ' is-good' : ' is-warn')}>{w9s.length ? 'On file' : 'Missing'}</span>
          <span className="hq-cd-s">{w9s.length ? `Uploaded ${shortDate(w9s[0].created_at)}` : 'Upload a signed PDF'}</span>
        </button>
      </div>

      {/* ── tabs ── */}
      <nav className="hq-cd-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key}
            className={tab === t.key ? 'on' : ''} onClick={() => goTab(t.key)}>
            {t.label}
            {counts[t.key] > 0 && <span className="hq-cd-tabcount">{counts[t.key]}</span>}
          </button>
        ))}
      </nav>

      {Flash}
      {notifyDlg && (() => { /* @@NOTIFY_PICK_v115_4@@ */
        const nowMs = Date.now();
        const soon = (client.events || []).filter((e) => e.starts_at && new Date(e.starts_at).getTime() >= nowMs - 12 * 3600 * 1000
          && new Date(e.starts_at).getTime() < nowMs + 45 * 24 * 3600 * 1000).length; // @@SHOW_DAYS_v115_8@@ keep in step with SHOW_DAYS in server/lib/report-notify.js
        const cur = (reportsByStore[notifyDlg.storeId] || []).find((r) => r.id === notifyDlg.rep.id) || notifyDlg.rep;
        return (
          <NotifyClientModal clientName={client.name} label={cur.reference || String(cur.period_start || '').slice(0, 10)}
            notifiedAt={cur.client_notified_at} notifiedCount={cur.client_notified_count}
            contacts={client.contacts || []} showsSoon={soon} busy={!!notifyBusy[notifyDlg.rep.id]} error={notifyErr}
            onSend={sendNotify} onPreview={previewNotify} onClose={() => setNotifyDlg(null)} />
        );
      })()}

      {/* ═════════════ OVERVIEW ═════════════ */}
      {tab === 'overview' && (
        <div className="hq-cd-grid">
          <div className="hq-cd-col">
            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Profile</h2>
                {canReadW9 && <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={beginEdit}>Edit</button>}
              </div>
              <dl className="hq-cd-dl">
                <dt>Genre</dt><dd>{client.genre || DASH}</dd>
                <dt>Hometown</dt><dd>{client.hometown || DASH}</dd>
                <dt>Designation code</dt><dd>{client.designation_code || <span className="hq-cd-muted">Not set — needed for reports &amp; label references</span>}</dd>
                <dt>Commission</dt><dd>{commission}%</dd>
                <dt>Opening balance</dt>
                <dd>{client.opening_balance == null ? DASH : `${money(client.opening_balance)}${client.opening_balance_date ? ' as of ' + String(client.opening_balance_date).slice(0, 10) : ''}`}</dd>
                <dt>Client shipping</dt><dd>{client.shipping_enabled ? 'Enabled — band can create FedEx labels billed to XYZ' : 'Disabled'}</dd>
                <dt>Shopify</dt><dd><Ext href={client.shopify_url}>{client.shopify_url ? client.shopify_url.replace(/^https?:\/\//, '').replace(/\/$/, '') : ''}</Ext></dd>
                <dt>Google Drive</dt><dd><Ext href={client.drive_url}>Open folder</Ext></dd>
                {client.shows_source === 'seated'
                  ? (<><dt>Seated ID</dt><dd>{client.seated_artist_id || DASH}</dd></>)
                  : (<><dt>Bandsintown ID</dt><dd>{client.bandsintown_artist_id || DASH}</dd></>)}
                <dt>Added</dt><dd>{shortDate(client.created_at)}</dd>
              </dl>
            </section>

            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Notes</h2></div>
              <div className="hq-cd-notes">{client.notes ? client.notes : <span className="hq-cd-muted">No notes.</span>}</div>
            </section>
          </div>

          <div className="hq-cd-col">
            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Zoho Books</h2>
                {canManageLinks && <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={refreshZohoCustomers}>Refresh list</button>}
              </div>
              <div className="hq-cd-pad">
                <div className="hq-cd-zoho">
                  {client.zoho_customer_id
                    ? <><span className="hq-cd-dot is-good" />Linked to <strong>{client.zoho_customer_name || client.zoho_customer_id}</strong></>
                    : <><span className="hq-cd-dot is-warn" />Not linked — the portal can't show statements or invoices yet.</>}
                </div>
                {canManageLinks && (
                  <div className="hq-cd-row">
                    <select className="hq-select" value={zohoPick} onChange={(e) => setZohoPick(e.target.value)} style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <option value="">{client.zoho_customer_id ? 'Change / unlink…' : 'Pick a Zoho customer…'}</option>
                      {client.zoho_customer_id && <option value="">— Unlink —</option>}
                      {zohoCustomers.map((c) => (
                        <option key={c.id} value={c.zoho_contact_id}>{c.name}{c.email ? ` (${c.email})` : ''}</option>
                      ))}
                    </select>
                    <button className="hq-btn" onClick={linkZoho}>Save link</button>
                    {!client.zoho_customer_id && (
                      <button className="hq-btn hq-btn-ghost" onClick={createAndLinkZoho}>Create in Zoho &amp; link</button>
                    )}
                  </div>
                )}
                {/* @@CN_ACCOUNT_v108@@ which Zoho account this client's credit notes (store reports + POS) post to */}
                {client.zoho_customer_id && (
                  <CnAccountPicker clientId={id} accountId={client.zoho_cn_account_id} accountName={client.zoho_cn_account_name}
                    canManage={canManageLinks} onSaved={(c) => setClient((cur) => ({ ...cur, ...c }))} />
                )}
              </div>
            </section>

            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Portal access</h2>
                <button className="hq-linkish" onClick={() => goTab('people')}>Manage →</button>
              </div>
              <ul className="hq-cd-list">
                {contacts.slice(0, 5).map((c) => (
                  <li key={c.id}>
                    <span>{personName(c) || c.email || 'Unnamed'}<small>{c.email || ''}</small></span>
                    <PortalPill c={c} />
                  </li>
                ))}
                {contacts.length === 0 && <li className="hq-cd-muted">No contacts yet — attach one to give portal access.</li>}
                {contacts.length > 5 && <li className="hq-cd-muted">+{contacts.length - 5} more</li>}
              </ul>
            </section>

            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Upcoming shows</h2>
                <button className="hq-linkish" onClick={() => goTab('shows')}>All shows →</button>
              </div>
              <ul className="hq-cd-list">
                {upcoming.slice(0, 3).map((ev) => (
                  <li key={ev.id}>
                    <span>{ev.venue_name || ev.title || 'Show'}<small>{[ev.city, ev.region].filter(Boolean).join(', ')}</small></span>
                    <span className="hq-cd-date">{shortDate(ev.starts_at)}</span>
                  </li>
                ))}
                {upcoming.length === 0 && <li className="hq-cd-muted">Nothing upcoming.</li>}
              </ul>
            </section>

            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head"><h2>Recent shipments</h2>
                <button className="hq-linkish" onClick={() => goTab('shipping')}>All →</button>
              </div>
              <ul className="hq-cd-list">
                {liveShipments.slice(0, 3).map((p) => (
                  <li key={p.id}>
                    <span>{p.label || p.tracking_number || 'Shipment'}<small>{(p.carrier || '').toUpperCase()} {p.tracking_number || ''}</small></span>
                    <span className={`hq-pill hq-status-${p.status}`}>{String(p.status || '').replace(/_/g, ' ')}</span>
                  </li>
                ))}
                {liveShipments.length === 0 && <li className="hq-cd-muted">No shipments yet.</li>}
              </ul>
            </section>
          </div>
        </div>
      )}

      {/* ═════════════ CONTACTS & PORTAL ═════════════ */}
      {tab === 'people' && (
        <section className="hq-panel hq-cd-card">
          <div className="hq-panel-head">
            <h2>Contacts &amp; portal access</h2>
            {canManageLinks && (
              <div className="hq-cd-row" style={{ padding: 0 }}>
                <select className="hq-select" value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 200 }}>
                  <option value="">Attach a contact…</option>
                  {available.map((c) => (
                    <option key={c.id} value={c.id}>{personName(c) || c.email || c.id}{c.email && personName(c) ? ` (${c.email})` : ''}</option>
                  ))}
                </select>
                <button className="hq-btn" onClick={attach} disabled={!pick}>Attach</button>
              </div>
            )}
          </div>
          <p className="hq-cd-help">
            Portal logins come from contacts. Invite a contact with an email and they get a login that sees this band.
            Client-portal shipping is <strong>{client.shipping_enabled ? 'on' : 'off'}</strong>{canReadW9 ? ' (change it in Edit profile)' : ''}.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="hq-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Portal</th><th></th></tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="hq-cd-strong">{personName(c) || DASH}</div>
                      {(c.title || c.company) && <div className="hq-cd-sub">{[c.title, c.company].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td>{c.email ? <a className="hq-cd-a" href={`mailto:${c.email}`}>{c.email}</a> : DASH}</td>
                    <td>{c.phone || DASH}</td>
                    <td><PortalPill c={c} /></td>
                    <td className="hq-cd-actcell">
                      {canManageLinks && !c.portal_active && (
                        c.email ? (
                          <button className="hq-btn hq-cd-sm" disabled={!!inviteBusy[c.id]} onClick={() => invite(c)}>
                            {inviteBusy[c.id] ? 'Sending…' : (c.portal_invited ? 'Resend invite' : 'Invite to portal')}
                          </button>
                        ) : <span className="hq-cd-muted hq-cd-small">Add an email to invite</span>
                      )}
                      {canManageLinks && (
                        <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => detach(c)}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && (
                  <tr><td className="hq-empty" colSpan={5}>No contacts attached.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ═════════════ SHOPIFY & REPORTS ═════════════ */}
      {tab === 'shopify' && canManageLinks && (
        <>
          {/* @@CSV_REPORTS_v117@@ period report from an uploaded Shopify orders CSV (no Shopify connection needed) */}
          {/* @@CSV_ONLY_UNCONNECTED_v117_1@@ only for clients without a connected store (connected stores generate from Shopify as before) */}
          {!stores.some((x) => !x.is_manual && x.has_token && x.shopify_client_id) && (
          <CsvReportPanel clientId={id} clientName={client.name} existing={reportsByStore[(stores.find((x) => x.is_manual) || {}).id] || []}
            onGenerated={async (storeId, reportId) => { await load(); await loadReports(storeId); setFocusReport((p) => ({ ...p, [storeId]: { id: reportId, t: Date.now() } })); }} />)}

          {stores.map((st) => (
            <section key={st.id} className="hq-panel hq-cd-card">
              <div className="hq-panel-head">
                <div>
                  <h2>{st.label || st.store_domain}</h2>
                  <div className="hq-cd-sub">
                    {st.label ? st.store_domain + ' · ' : ''}
                    {st.is_manual ? <span className="hq-cd-muted">Reports from CSV uploads</span> : <span className={st.has_token ? 'hq-cd-good' : 'hq-cd-warn'}>{st.has_token ? 'Credentials set' : 'No credentials'}</span>}
                    {st.api_scopes ? ` · ${st.api_scopes}` : ''}
                  </div>
                </div>
                <div className="hq-cd-row" style={{ padding: 0 }}>
                  <button className={'hq-btn hq-cd-sm' + (reportsByStore[st.id] ? '' : ' hq-btn-ghost')} onClick={() => toggleReports(st.id)}>Reports</button>
                  {!st.is_manual && (<>{/* @@CSV_REPORTS_v117@@ these need Shopify credentials */}
                  <button className={'hq-btn hq-cd-sm' + (salesByStore[st.id] ? '' : ' hq-btn-ghost')} onClick={() => viewSales(st.id)}>Sales (12 mo)</button>
                  <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => downloadInventoryPdf(st.id)}>Inventory PDF</button>
                  <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => replaceStoreToken(st.id)}>Replace token</button></>)}
                  <button className="hq-btn hq-btn-ghost hq-cd-sm hq-cd-danger" onClick={() => removeStore(st.id)}>Remove</button>
                </div>
              </div>

              {salesByStore[st.id] && (
                <div className="hq-cd-sub-block">
                  <h3>Monthly sales</h3>
                  {salesByStore[st.id].rows.length === 0 ? (
                    <p className="hq-cd-muted">No sales data returned.</p>
                  ) : (
                    <table className="hq-table">
                      <thead><tr><th>Month</th><th style={{ textAlign: 'right' }}>Net sales</th><th style={{ textAlign: 'right' }}>Taxes</th></tr></thead>
                      <tbody>
                        {salesByStore[st.id].rows.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.month || DASH}</td>
                            <td style={{ textAlign: 'right' }}>{r.net_sales != null ? r.net_sales : DASH}</td>
                            <td style={{ textAlign: 'right' }}>{r.taxes != null ? r.taxes : DASH}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {reportsByStore[st.id] && (
                <div className="hq-cd-sub-block">
                  <h3>Period reports</h3>
                  {st.is_manual ? (<p className="hq-cd-small hq-cd-muted" style={{ margin: '0 0 12px' }}>{/* @@CSV_REPORTS_v117@@ */}These reports come from CSV uploads (the panel above). To redo a month, upload it again.</p>) : (
                  <div className="hq-cd-row" style={{ padding: '0 0 12px' }}>
                    <select className="hq-select" value={reportForm.period_type}
                      onChange={(e) => setReportForm({ period_type: e.target.value, period_start: '' })}>
                      <option value="month">Month</option>
                      <option value="quarter">Quarter</option>
                    </select>
                    {/* @@REPORT_PERIODS_v115_1@@ pick the period from a list; opens on the last completed one */}
                    {(() => {
                      const existing = reportsByStore[st.id] || [];
                      const sel = resolvePeriod(reportForm.period_type, reportForm.period_start, existing);
                      return (
                        <select className="hq-select" value={sel} title="Period to report on"
                          onChange={(e) => setReportForm({ ...reportForm, period_start: e.target.value })}>
                          {periodOptions(reportForm.period_type, existing).options.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}{o.inProgress ? ' — in progress' : ''}{o.done ? ' ✓' : ''}</option>
                          ))}
                        </select>
                      );
                    })()}
                    <button className="hq-btn" disabled={reportBusy} onClick={() => generateReport(st.id)}>
                      {reportBusy ? 'Generating… (~30-60s)' : 'Generate'}
                    </button>
                    <span className="hq-cd-divider" />
                    <span className="hq-cd-small hq-cd-muted">PDFs include:</span>
                    <label className="hq-cd-check">
                      <input type="checkbox" checked={reportOpts.cogs}
                        onChange={(e) => setReportOpts((o) => ({ ...o, cogs: e.target.checked }))} /> <span title="Generate: deducts cost of goods (current Shopify product cost) from the artist payout and freezes it. PDF: adds the COGS section. Regenerate a report to change its payout.">COGS</span>
                    </label>
                    <label className="hq-cd-check">
                      <input type="checkbox" checked={reportOpts.inventory}
                        onChange={(e) => setReportOpts((o) => ({ ...o, inventory: e.target.checked }))} /> Inventory
                    </label>
                  </div>)}
                  <ReportsReview
                      clientId={id} storeId={st.id} reports={reportsByStore[st.id]} isManager={canManageLinks}
                      focus={focusReport[st.id]} cnBusy={cnBusy} notifyBusy={notifyBusy}
                      onPdf={downloadReportPdf} onCreditNote={generateCreditNote} onNotify={notifyReport} onDelete={deleteReport}
                      onLinkExisting={() => linkExistingCreditNotes(st.id)} linkBusy={!!linkBusy[st.id]} />{/* @@REPORT_REVIEW_v115@@ */}
                </div>
              )}


            </section>
          ))}

          <section className="hq-panel hq-cd-card">
            <div className="hq-panel-head">
              <h2>{stores.length ? 'Add another store' : 'Connect a Shopify store'}</h2>
              <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => setShowAddStore((s) => !s)}>{showAddStore ? 'Cancel' : 'Add store'}</button>
            </div>
            {showAddStore ? (
              <div className="hq-cd-formgrid hq-cd-pad">
                <label>Store domain<input className="hq-input" placeholder="store.myshopify.com" value={storeForm.store_domain}
                  onChange={(e) => setStoreForm({ ...storeForm, store_domain: e.target.value })} /></label>
                <label><span>Label <em>optional</em></span><input className="hq-input" placeholder="e.g. Tour store" value={storeForm.label}
                  onChange={(e) => setStoreForm({ ...storeForm, label: e.target.value })} /></label>
                <label>Client ID<input className="hq-input" value={storeForm.shopify_client_id}
                  onChange={(e) => setStoreForm({ ...storeForm, shopify_client_id: e.target.value })} /></label>
                <label>Client secret<input className="hq-input" type="password" autoComplete="off" value={storeForm.access_token}
                  onChange={(e) => setStoreForm({ ...storeForm, access_token: e.target.value })} /></label>
                <label><span>Scopes <em>optional</em></span><input className="hq-input" value={storeForm.api_scopes}
                  onChange={(e) => setStoreForm({ ...storeForm, api_scopes: e.target.value })} /></label>
                <div className="hq-cd-formend"><button className="hq-btn" onClick={addStore}>Save store</button></div>
              </div>
            ) : (
              stores.length === 0 && <p className="hq-cd-help">No Shopify store linked yet.</p>
            )}
          </section>
        </>
      )}

      {/* ═════════════ SHIPPING ═════════════ */}
      {tab === 'shipping' && (
        <>
          {canManageLinks && (
            <section className="hq-panel hq-cd-card">
              <div className="hq-panel-head">
                <h2>Create FedEx label</h2>
                <button className="hq-btn" onClick={createLabel} disabled={labelBusy}>{labelBusy ? 'Creating…' : 'Create label'}</button>
              </div>
              <div className="hq-cd-pad">
                <p className="hq-cd-help" style={{ padding: 0, margin: '0 0 14px' }}>
                  Ships from the default From to the default To (set in Settings). Label reference:{' '}
                  {client.designation_code
                    ? <><strong>{client.designation_code}</strong> – your description.</>
                    : <>your description (this band has no designation code yet).</>}
                </p>
                <input className="hq-input" placeholder="Description (e.g. tour tees)" value={labelForm.description}
                  onChange={(e) => setLabelForm({ ...labelForm, description: e.target.value })} style={{ width: '100%', maxWidth: 420 }} />
                <div className="hq-cd-boxes">
                  {boxes.map((bx, i) => (
                    <div className="hq-cd-box" key={i}>
                      <span className="hq-cd-boxn">Box {i + 1}</span>
                      <select className="hq-select" defaultValue="" onChange={(e) => { applyParcelPresetToBox(i, e.target.value); e.target.value = ''; }}>
                        <option value="">Preset…</option>
                        {parcelPresets.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                      </select>
                      <label>lb<input className="hq-input" inputMode="decimal" value={bx.weight_lbs} onChange={(e) => setBox(i, { weight_lbs: e.target.value })} /></label>
                      <label>L<input className="hq-input" inputMode="decimal" value={bx.length_in} onChange={(e) => setBox(i, { length_in: e.target.value })} /></label>
                      <label>W<input className="hq-input" inputMode="decimal" value={bx.width_in} onChange={(e) => setBox(i, { width_in: e.target.value })} /></label>
                      <label>H<input className="hq-input" inputMode="decimal" value={bx.height_in} onChange={(e) => setBox(i, { height_in: e.target.value })} /></label>
                      {boxes.length > 1 && (
                        <button className="hq-cd-x" onClick={() => removeBox(i)} title="Remove box" aria-label={`Remove box ${i + 1}`}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="hq-cd-row" style={{ padding: '10px 0 0' }}>
                  <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={addBox}>+ Add box</button>
                  {boxes.length > 1 && <span className="hq-cd-small hq-cd-muted">{boxes.length} boxes — one shipment, linked tracking. (To change the count after creating, delete and recreate.)</span>}
                </div>
                {labelMsg && <p className="hq-msg">{labelMsg}</p>}
              </div>
            </section>
          )}

          <section className="hq-panel hq-cd-card">
            <div className="hq-panel-head"><h2>Shipping history</h2></div>
            {!canManageLinks && labelMsg && <p className="hq-msg" style={{ padding: '0 20px' }}>{labelMsg}</p>}
            <div style={{ overflowX: 'auto' }}>
              <table className="hq-table">
                <thead><tr><th>Label</th><th>Carrier</th><th>Tracking</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {shipments.map((p) => (
                    <tr key={p.id} style={p.archived_at ? { opacity: 0.55 } : null}>
                      <td>{p.label || DASH}{p.archived_at ? ' (archived)' : ''}</td>
                      <td style={{ textTransform: 'uppercase' }}>{p.carrier}</td>
                      <td>{p.tracking_number || DASH}</td>
                      <td><span className={`hq-pill hq-status-${p.status}`}>{String(p.status || '').replace(/_/g, ' ')}</span></td>
                      <td className="hq-cd-actcell">
                        {p.has_label_file
                          ? <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => downloadShipmentLabel(p)}>PDF</button>
                          : <span className="hq-cd-muted hq-cd-small">no pdf</span>}
                        {p.has_zpl_file && <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => downloadShipmentZpl(p)}>ZPL</button>}
                        <button className="hq-btn hq-btn-ghost hq-cd-sm hq-cd-danger" onClick={() => deleteShipment(p)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {shipments.length === 0 && (
                    <tr><td className="hq-empty" colSpan={5}>No shipments for this band yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* ═════════════ SHOWS ═════════════ */}
      {tab === 'shows' && (
        <section className="hq-panel hq-cd-card">
          <div className="hq-panel-head">
            <div>
              <h2>Shows</h2>
              <div className="hq-cd-sub">
                {client.shows_source === 'seated' ? (
                  <>Source Seated{' · '}Artist ID {client.seated_artist_id || 'not set'}</>
                ) : (
                  <>
                    Source Bandsintown{' · '}Bandsintown key <span className={client.has_api_key ? 'hq-cd-good' : 'hq-cd-warn'}>{client.has_api_key ? 'set' : 'not set'}</span>
                    {' · '}Artist ID {client.bandsintown_artist_id || 'not set'}
                  </>
                )}
                {' · '}Last synced {client.events_synced_at ? dateTime(client.events_synced_at) : 'never'}
              </div>
            </div>
            <button className="hq-btn" onClick={refreshEvents} disabled={busy}>{busy ? 'Syncing…' : 'Refresh shows'}</button>
          </div>
          {canReadW9 && client.shows_source !== 'seated' && (
            <div className="hq-cd-row hq-cd-pad" style={{ paddingTop: 0 }}>
              <input className="hq-input" type="password" autoComplete="off" style={{ flex: '1 1 240px', maxWidth: 360 }}
                placeholder={client.has_api_key ? 'Replace Bandsintown API key' : 'Add Bandsintown API key'}
                value={keyInput} onChange={(e) => setKeyInput(e.target.value)} />
              <button className="hq-btn hq-btn-ghost" onClick={saveKey} disabled={!keyInput.trim()}>Save key</button>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="hq-table">
              <thead><tr><th>Date</th><th>Venue</th><th>Location</th><th>Event</th><th>Tickets</th></tr></thead>
              <tbody>
                {(client.events || []).map((ev) => (
                  <tr key={ev.id}>
                    <td className="hq-cd-date">{dateTime(ev.starts_at)}</td>
                    <td className="hq-cd-strong">{ev.venue_name || DASH}</td>
                    <td className="hq-cd-muted">{[ev.city, ev.region, ev.country].filter(Boolean).join(', ') || DASH}</td>
                    <td>{ev.event_url ? <Ext href={ev.event_url}>{ev.title || 'Event'}</Ext> : (ev.title || DASH)}</td>
                    <td>{ev.ticket_url ? <Ext href={ev.ticket_url}>Tickets</Ext> : DASH}</td>
                  </tr>
                ))}
                {(!client.events || client.events.length === 0) && (
                  <tr><td className="hq-empty" colSpan={5}>No shows cached. Hit “Refresh shows”.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ═════════════ W9 & PAYMENT ═════════════ */}
      {tab === 'docs' && (
        <>
          <section className="hq-panel hq-cd-card">
            <div className="hq-panel-head">
              <h2>Signed W9 / W-8 PDFs</h2>{/* @@PAYMENT_INFO_W8_v105@@ */}
              <div>
                <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                  onChange={(e) => { uploadW9(e.target.files[0]); e.target.value = ''; }} />
                <button className="hq-btn" onClick={() => fileRef.current && fileRef.current.click()}>Upload PDF</button>
              </div>
            </div>
            <table className="hq-table">
              <thead><tr><th>File</th><th>Size</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {w9s.map((w) => (
                  <tr key={w.id}>
                    <td>{w.original_name}</td>
                    <td>{Math.max(1, Math.round((w.byte_size || 0) / 1024))} KB</td>
                    <td>{shortDate(w.created_at)}</td>
                    <td className="hq-cd-actcell">
                      {canReadW9 ? (
                        <>
                          <button className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => downloadW9(w)}>Download</button>
                          <button className="hq-btn hq-btn-ghost hq-cd-sm hq-cd-danger" onClick={() => deleteW9(w)}>Delete</button>
                        </>
                      ) : (
                        <span className="hq-cd-muted hq-cd-small">admin/manager only</span>
                      )}
                    </td>
                  </tr>
                ))}
                {w9s.length === 0 && (
                  <tr><td className="hq-empty" colSpan={4}>No signed W9 / W-8 PDF on file.</td></tr>
                )}
              </tbody>
            </table>
          </section>
          <PaymentInfoStaffPanel clientId={id} />
        </>
      )}

      {/* ═════════════ EDIT DIALOG ═════════════ */}
      {editing && edit && (
        <div className="hq-cd-modal" role="dialog" aria-modal="true" aria-label="Edit client profile"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(false); }}>
          <div className="hq-cd-dialog">
            <div className="hq-cd-dialoghead">
              <h2>Edit profile</h2>
              <button className="hq-cd-x" onClick={() => setEditing(false)} aria-label="Close">×</button>
            </div>
            <div className="hq-cd-dialogbody">
              <fieldset>
                <legend>Profile</legend>
                <div className="hq-cd-formgrid">
                  <label className="span2">Band / company name<input className="hq-input" value={edit.name} onChange={setE('name')} autoFocus /></label>
                  <label>Genre<input className="hq-input" value={edit.genre} onChange={setE('genre')} /></label>
                  <label>Hometown<input className="hq-input" value={edit.hometown} onChange={setE('hometown')} /></label>
                  <label>Status
                    <select className="hq-select" value={edit.status} onChange={setE('status')}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="span2">Notes<textarea className="hq-input hq-cd-textarea" rows={4} value={edit.notes} onChange={setE('notes')} /></label>
                </div>
              </fieldset>
              <fieldset>
                <legend>Business</legend>
                <div className="hq-cd-formgrid">
                  <label>Designation code<input className="hq-input" value={edit.designation_code} onChange={setE('designation_code')} /></label>
                  <label><span>Commission % <em>XYZ's cut</em></span><input className="hq-input" type="number" min="0" max="100" value={edit.commission_pct} onChange={setE('commission_pct')} /></label>
                  <label>Opening balance<input className="hq-input" type="number" step="0.01" value={edit.opening_balance} onChange={setE('opening_balance')} /></label>
                  <label>Balance as of<input className="hq-input" type="date" value={edit.opening_balance_date} onChange={setE('opening_balance_date')} /></label>
                </div>
              </fieldset>
              <fieldset>
                <legend>Links</legend>
                <div className="hq-cd-formgrid">
                  <label className="span2">Shopify store URL<input className="hq-input" value={edit.shopify_url} onChange={setE('shopify_url')} placeholder="https://" /></label>
                  <label className="span2">Google Drive URL<input className="hq-input" value={edit.drive_url} onChange={setE('drive_url')} placeholder="https://" /></label>
                  <label>Shows source
                    <select className="hq-select" value={edit.shows_source} onChange={setE('shows_source')}>
                      <option value="bandsintown">Bandsintown</option>
                      <option value="seated">Seated</option>
                    </select>
                  </label>
                  {edit.shows_source === 'seated'
                    ? <label>Seated artist ID<input className="hq-input" value={edit.seated_artist_id} onChange={setE('seated_artist_id')} placeholder="Artist UUID, or paste a Seated URL containing it" /></label>
                    : <label>Bandsintown artist ID<input className="hq-input" value={edit.bandsintown_artist_id} onChange={setE('bandsintown_artist_id')} /></label>}
                </div>
              </fieldset>
              <fieldset>
                <legend>Client portal</legend>
                <label className="hq-cd-check hq-cd-checkline">
                  <input type="checkbox" checked={!!edit.shipping_enabled} onChange={setE('shipping_enabled')} />
                  <span>Allow client-portal shipping<small>Any portal login for this band can create FedEx labels billed to XYZ.</small></span>
                </label>
              </fieldset>
              {editMsg && <p className="hq-msg">{editMsg}</p>}
            </div>
            <div className="hq-cd-dialogfoot">
              <button className="hq-btn hq-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button className="hq-btn" onClick={saveEdits} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
